import type { AxiosError } from 'axios';
import AppError from '@sharedtypes/errorTypes';

interface ErrorResponseData {
  message?: string;
  error?: string;
  [key: string]: unknown;
}

/**
 * Creates AppError objects for client-side use
 */
class ClientAppError extends AppError {
  constructor(message: string, statusCode: number = 500, errorType: string = 'general', details?: unknown) {
    super(message, statusCode, errorType, details);
    this.name = 'ClientAppError';
  }

  /**
   * Creates an error for network connectivity issues
   * @param message - Error message
   * @param details - Additional error details
   */
  static networkError(message: string = 'Network connection error', details?: unknown): ClientAppError {
    return new ClientAppError(message, 503, 'network_error', details);
  }

  /**
   * Creates an error for configuration issues
   * @param message - Error message
   * @param details - Additional error details
   */
  static configError(message: string, details?: unknown): ClientAppError {
    return new ClientAppError(message, 500, 'config_error', details);
  }

  /**
   * Creates an error for authentication issues
   * @param message - Error message
   * @param details - Additional error details
   */
  static authError(message?: string, details?: unknown): ClientAppError {
    return new ClientAppError(message || 'Authentication failed', 401, 'auth_error', details);
  }

  /**
   * Creates an error for invalid input
   * @param message - Error message
   * @param details - Additional error details
   */
  static invalidInput(message: string, details?: unknown): ClientAppError {
    return new ClientAppError(message, 400, 'validation', details);
  }

  /**
   * Creates an error for UI/component rendering issues
   * @param message - Error message
   * @param details - Additional error details
   */
  static renderingError(message: string, details?: unknown): ClientAppError {
    return new ClientAppError(message, 500, 'rendering_error', details);
  }

  /**
   * Creates an error from an axios error
   * @param error - The original Axios error
   * @param defaultMessage - Default message if none available in error
   */
  static fromAxiosError(error: AxiosError, defaultMessage?: string): ClientAppError {
    const status = error.response?.status || 500;

    // Cast response data to a typed interface to fix the TypeScript error
    const responseData = error.response?.data as ErrorResponseData | undefined;
    const message = responseData?.message || error.message || defaultMessage || 'API request failed';

    const details = {
      config: error.config,
      data: error.response?.data,
    };

    return new ClientAppError(message, status, 'api_error', details);
  }

  /**
   * Creates an error from any type of error
   * @param error - The original error
   * @param defaultMessage - Default message if none available in error
   * @param defaultStatus - Default status code
   */
  static fromError(error: unknown, defaultMessage: string = 'An error occurred', defaultStatus: number = 500): ClientAppError {
    // If it's already a ClientAppError, return it
    if (error instanceof ClientAppError) {
      return error;
    }

    // If it's an AppError, convert it
    if (error instanceof AppError) {
      return new ClientAppError(error.message, error.statusCode, error.errorType, error.details);
    }

    // If it's an Error, use its message
    if (error instanceof Error) {
      return new ClientAppError(error.message || defaultMessage, defaultStatus, 'client_error', error);
    }

    // For unknown errors
    return new ClientAppError(defaultMessage, defaultStatus, 'unknown_error', error);
  }
}

// Create a typed constant to avoid module resolution issues
const ClientAppErrorClass = ClientAppError;

// Export with explicit type to help TypeScript understand the structure
export default ClientAppErrorClass as typeof ClientAppError;
